"use client";

import { toast } from "sonner";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/convex/_generated/api";
import { Button } from "@/app/v2/_components/button/button";
import { useMutation, useQuery } from "convex/react";
import { CodeXml, Download, EyeIcon, Loader2, Trash2, Upload, View } from "lucide-react";
import { cn } from "@/lib/utils";
import { EmptyState } from "../utils/empty-state";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { uploadToCloudflareWithS3 } from "@/actions/cloudflare/upload-to-cloudflare-with-s3";
import { useLogger } from "@/lib/axiom/client";

export default function SettingsContactImportPage() {
  const [isUploading, setIsUploading] = useState(false);
  const downloadSampleCsvUrl = `https://files.safemail.co/sample_contact_import.csv`;

  const createCsvImport = useMutation(api.importer.csvImport.createCsvImport);
  const csvImports = useQuery(api.importer.csvImport.listCsvImports);
  const deleteCsvImport = useMutation(api.importer.csvImport.deleteCsvImport);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const log = useLogger();

  useEffect(() => {
    if (confirmDelete) {
      setTimeout(() => {
        setConfirmDelete(null);
      }, 3000);
    }
  }, [confirmDelete]);

  const handleFileUpload = async (file: File) => {
    // Check filetype is CSV
    if (file.type !== "text/csv") {
      toast.error("File type must be CSV");
      return;
    }

    try {
      setIsUploading(true);
      const { url, key } = await uploadToCloudflareWithS3(file, {
        type: "contactImport",
      });

      log.info("CSV uploaded successfully", { url, key });

      await createCsvImport({
        url,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        metadata: {
          type: "contactImport",
        },
      });

      toast.success("CSV uploaded successfully");
    } catch (error) {
      log.error("Failed to upload CSV", { error });
      toast.error("Failed to upload CSV");
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFileButton = (hidden: boolean) => {
    return (
      <div>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <Input
              ref={inputRef}
              type="file"
              accept="text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(file);
                }
              }}
              disabled={isUploading}
              className={cn("w-full", hidden && "hidden")}
              inputSize="default"
            />
          </div>
        </div>
        {!hidden && isUploading && <div className="mt-4 text-sm text-muted-foreground">Uploading...</div>}
      </div>
    );
  };

  const downloadSampleCsvButton = () => {
    return (
      <div className="flex flex-col items-start">
        <div className="text-muted-foreground mb-4">Here are the available fields for the CSV:</div>
        <pre className="text-sm text-muted-foreground">
          {`+----------------+-----------+----------------------------------------+
| Key            | Required? | Notes                                  |
+----------------+-----------+----------------------------------------+
| email_address  | Yes       | Contact’s primary email                |
| full_name      | No        | Full name (alternative to first/last)  |
| first_name     | No        | First/given name                       |
| last_name      | No        | Last/family name                       |
| status         | No        | Must be 'subscribed' or 'unsubscribed' |
| tags           | No        | Comma- or semicolon-separated labels   |
| address        | No        | Mailing address                        |
| list           | No        | List name for subscription             |
+----------------+-----------+----------------------------------------+
`}
        </pre>
      </div>
    );
  };

  return (
    <>
      {!csvImports || csvImports?.length === 0 ? (
        <div className="w-full">
          <EmptyState
            title="Import Contacts"
            description="Import contacts from a CSV file."
            icon={<CodeXml className="h-16 w-16" />}
            buttonIcon={<CodeXml className="h-4 w-4" />}
            className="sm:w-full items-center justify-center"
          >
            {" "}
            <div className="flex justify-center items-center p-8 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  window.open(downloadSampleCsvUrl, "_blank");
                }}
              >
                Download Sample CSV <Download className="h-4 w-4" />
              </Button>
              {uploadFileButton(true)}
              <Button
                size="sm"
                disabled={isUploading}
                onClick={() => {
                  inputRef.current?.click();
                }}
              >
                Upload CSV {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              </Button>
            </div>
          </EmptyState>
        </div>
      ) : (
        <div className="flex flex-row gap-8 w-full">
          <Card className="w-full">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div className="flex flex-col gap-2">
                  <CardTitle>CSV Imports</CardTitle>
                  <CardDescription>Manage your CSV imports</CardDescription>
                </div>
                <div className="flex justify-center items-center p-8 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.open(downloadSampleCsvUrl, "_blank");
                    }}
                  >
                    Download Sample CSV <Download className="h-4 w-4" />
                  </Button>
                  {uploadFileButton(true)}
                  <Button
                    size="sm"
                    disabled={isUploading}
                    onClick={() => {
                      inputRef.current?.click();
                    }}
                  >
                    Upload CSV{" "}
                    {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Total Contacts</TableHead>
                    <TableHead>Processed Contacts</TableHead>
                    <TableHead>Actions</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvImports?.map((csvImport) => (
                    <TableRow key={csvImport._id}>
                      <TableCell>{csvImport.name}</TableCell>
                      <TableCell>{csvImport.status}</TableCell>
                      <TableCell>{csvImport.totalContacts}</TableCell>
                      <TableCell>{csvImport.processedContacts}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={!csvImport.csvFileUrl}
                            onClick={async () => {
                              window.open(csvImport.csvFileUrl, "_blank");
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={async () => {
                              await deleteCsvImport({ id: csvImport._id });
                              toast.success("Import file deleted");
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>{csvImport.statusMessage}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
